<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { SeatmapZone } from '~/lib/seatmapeditor/types'

interface Props {
	isOpen: boolean
	zones: SeatmapZone[]
	busy?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	busy: false
})

const { $translate } = useNuxtApp()
const _ = $translate as (key: string) => string

const emit = defineEmits<{
	(e: 'close'): void
	(e: 'add'): void
	(e: 'rename', payload: { id: number; name: string }): void
	(e: 'delete', id: number): void
}>()

const pendingDeleteId = ref<number | null>(null)
const draftNames = ref<Record<number, string>>({})
const nameInputRefs = ref<Record<number, HTMLInputElement | null>>({})

const setNameInputRef = (zoneId: number, el: unknown) => {
	if (el && typeof el === 'object' && 'focus' in el) {
		nameInputRefs.value[zoneId] = el as HTMLInputElement
	}
}

watch(
	() => props.zones,
	(zones, previous) => {
		const next: Record<number, string> = {}
		zones.forEach((zone) => {
			next[zone.id] = draftNames.value[zone.id] ?? zone.name
		})
		draftNames.value = next

		const previousIds = new Set((previous ?? []).map((zone) => zone.id))
		const added = zones.find((zone) => !previousIds.has(zone.id))
		if (added) {
			nextTick(() => {
				nameInputRefs.value[added.id]?.focus()
				nameInputRefs.value[added.id]?.select()
			})
		}
	},
	{ immediate: true, deep: true }
)

watch(
	() => props.isOpen,
	(open) => {
		if (!open) {
			pendingDeleteId.value = null
		}
	}
)

const pendingDeleteZone = computed(() => {
	if (pendingDeleteId.value == null) {
		return null
	}
	return props.zones.find((zone) => zone.id === pendingDeleteId.value) ?? null
})

const onNameBlur = (zone: SeatmapZone) => {
	const nextName = String(draftNames.value[zone.id] ?? '').trim()
	if (!nextName || nextName === zone.name) {
		draftNames.value = {
			...draftNames.value,
			[zone.id]: zone.name
		}
		return
	}
	emit('rename', { id: zone.id, name: nextName })
}

const requestDelete = (zoneId: number) => {
	pendingDeleteId.value = zoneId
}

const cancelDelete = () => {
	pendingDeleteId.value = null
}

const confirmDelete = () => {
	if (pendingDeleteId.value == null) {
		return
	}
	emit('delete', pendingDeleteId.value)
	pendingDeleteId.value = null
}
</script>

<template>
	<div v-if="isOpen" class="modal fade in show zone-manager-modal" role="dialog" aria-modal="true" aria-labelledby="zoneManagerTitle">
		<div class="modal-dialog" role="document">
			<div class="modal-content">
				<div class="modal-header">
					<button type="button" class="close" :aria-label="_('Close')" :disabled="busy" @click="emit('close')"><span aria-hidden="true">&times;</span></button>
					<h4 class="modal-title" id="zoneManagerTitle">{{ _('Manage zones') }}</h4>
				</div>

				<div class="modal-body">
					<template v-if="pendingDeleteZone">
						<p>
							{{ _('Are you sure you want to delete this zone? Seats assigned to it will use Default after seat data is regenerated.') }}
						</p>
						<p v-if="pendingDeleteZone.name" class="text-muted">
							<strong>{{ pendingDeleteZone.name }}</strong>
						</p>
					</template>
					<template v-else>
						<p v-if="zones.length === 0" class="text-muted">{{ _('No zones yet.') }}</p>
						<ul v-else class="list-group zone-list">
							<li v-for="zone in zones" :key="zone.id" class="list-group-item zone-list-item">
								<input
									:ref="(el) => setNameInputRef(zone.id, el)"
									v-model="draftNames[zone.id]"
									type="text"
									class="form-control"
									:disabled="busy"
									:aria-label="_('Name')"
									@blur="onNameBlur(zone)"
									@keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
								/>
								<button
									type="button"
									class="btn btn-danger btn-sm"
									:disabled="busy"
									:aria-label="_('Delete')"
									@click="requestDelete(zone.id)"
								>
									<i class="fa fa-fw fa-trash"></i>
								</button>
							</li>
						</ul>
					</template>
				</div>

				<div class="modal-footer">
					<template v-if="pendingDeleteZone">
						<button type="button" class="btn btn-default" :disabled="busy" @click="cancelDelete">
							<i class="fa fa-fw fa-ban"></i> {{ _('Cancel') }}
						</button>
						<button type="button" class="btn btn-danger" :disabled="busy" @click="confirmDelete">
							<i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}
						</button>
					</template>
					<template v-else>
						<button type="button" class="btn btn-success" :disabled="busy" @click="emit('add')">
							<i class="fa fa-fw fa-plus"></i> {{ _('Add zone') }}
						</button>
						<button type="button" class="btn btn-default" :disabled="busy" @click="emit('close')">
							<i class="fa fa-fw fa-close"></i> {{ _('Close') }}
						</button>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.zone-manager-modal {
	z-index: 2020;
}

.zone-list {
	margin-bottom: 0;
	max-height: 50vh;
	overflow-y: auto;
}

.zone-list-item {
	display: flex;
	align-items: center;
	gap: 8px;
}

.zone-list-item .form-control {
	flex: 1;
}
</style>
